'use client'

import { useMemo } from 'react'

import { mainnet } from 'wagmi/chains'

import { useAutomations } from '~/app/automations/useAutomations'
import { SupportedChainId } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { useActiveOrg } from '~/hooks/org'
import { useContactLookup } from '~/hooks/useContacts'
import { useAddressLookup } from '~/hooks/useENS'
import { useFarcasterLookup } from '~/hooks/useFarcaster'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { HIDDEN_ADDRESS } from '~/store/privacyModeStore'
import { IAddress, SmartAccountRole } from '~/types'
import { Address } from '~/utils/address'
import { shortenAddress, shortenENS } from '~/utils/addresses'

export function useDisplayName(
  address?: IAddress,
  chainId: SupportedChainId = mainnet.id,
  shouldShortenAddress: boolean = true,
) {
  const { org } = useActiveOrg()
  const { smartAccounts } = useSmartAccounts()
  const { automations } = useAutomations()

  const { data: ens } = useAddressLookup(address, chainId)
  const isPrivacyModeOn = usePrivacyMode()
  const { farcaster } = useFarcasterLookup(address)
  const { data: contact } = useContactLookup(address)
  const { payee } = usePayeeLookupByAddress(address)

  const isTreasury = useMemo(() => {
    return smartAccounts.some(
      (account) =>
        account.address === address &&
        account.role === SmartAccountRole.TREASURY,
    )
  }, [smartAccounts, address])

  const bankAccount = useMemo(() => {
    if (org?.bridge?.externalAccounts)
      return org.bridge?.externalAccounts.find(({ liquidationAddress }) =>
        Address.equal(address, liquidationAddress),
      )
  }, [org, address])

  const automation = useMemo(
    () =>
      automations.find(
        ({ accountAddress }) =>
          accountAddress && Address.equal(address, accountAddress),
      ),
    [automations, address],
  )

  const displayName = useMemo(() => {
    if (!address || !Address.validate(address)) {
      return address
    }

    if (bankAccount) {
      return bankAccount.name
    }

    if (automation) {
      return automation.name
    }

    const baseName =
      contact?.smartAccountName ??
      contact?.orgLabel ??
      (ens ? shortenENS(ens) : undefined) ??
      farcaster?.displayName ??
      (farcaster?.username ? `@${farcaster.username}` : undefined) ??
      payee?.payeeOrg?.name ??
      payee?.payeeInvitedEmail ??
      (isPrivacyModeOn ? HIDDEN_ADDRESS : undefined) ??
      (shouldShortenAddress ? shortenAddress(address!) : address)

    return isTreasury ? `${baseName} ★` : baseName
  }, [
    address,
    contact?.orgLabel,
    contact?.smartAccountName,
    ens,
    farcaster?.displayName,
    farcaster?.username,
    payee?.payeeInvitedEmail,
    payee?.payeeOrg?.name,
    shouldShortenAddress,
    bankAccount,
    automation,
    isPrivacyModeOn,
    isTreasury,
  ])

  return useMemo(
    () => ({
      displayName,
      smartAccountName: contact?.smartAccountName,
      orgLabel: contact?.orgLabel,
      ens,
      farcaster,
      payee,
      bankAccount,
      automation,
      isTreasury,
    }),
    [
      displayName,
      contact?.smartAccountName,
      contact?.orgLabel,
      ens,
      farcaster,
      payee,
      bankAccount,
      automation,
      isTreasury,
    ],
  )
}
